<script setup>
    import { defineProps, computed } from 'vue';
    import { handleMarketplaceUrl } from '@/services/api';
    const props = defineProps({
      title: {
        type: String,
        required: true
      },
      badge: {
        type: String,
        default: ''
      },
      badgeColor: {
        type: String,
        default: ''
      },
      content: {
        type: String,
        default: ''
      },
      footerText: {
        type: String,
        default: ''
      },
      imageSrc: {
        type: String,
        default: ''
      },
      imageAlt: {
        type: String,
        default: ''
      },
      itemTitle: {
        type: String,
        default: ''
      },
      itemId: {
        type: [String, Number],
        default: null
      },
      userBadges: {
        type: Array,
        default: () => []
      },
      badges: {
        type: Array,
        default: () => []
      },
      tag: {
        type: Object,
        default: null
      },
      username: {
        type: String,
        default: ''
      }
    })

    const hexToRgba = (hex, alpha = 0.16) => {
      if (!hex) return '';
      let cleanHex = hex.replace('#', '');

      if (cleanHex.length === 3) {
        cleanHex = cleanHex.split('').map((c) => c + c).join('');
      }

      if (cleanHex.length !== 6) return hex;

      const r = Number.parseInt(cleanHex.slice(0, 2), 16);
      const g = Number.parseInt(cleanHex.slice(2, 4), 16);
      const b = Number.parseInt(cleanHex.slice(4, 6), 16);

      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const badgeStyles = computed(() => {
      if (!props.badgeColor) return {};
      return {
        backgroundColor: hexToRgba(props.badgeColor, 0.16),
        borderColor: props.badgeColor,
        color: props.badgeColor,
      };
    });

    const truncatedContent = computed(() => {
      const text = props.content || '';
      if (text.length > 300) {
        return text.substring(0, 300) + '...';
      }
      return text;
    });

    const getBadgeInitials = (badge) => {
      if (!badge.name) return '';
      const words = badge.name.trim().split(/\s+/);
      if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
      }
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    };

    const getItemBadgeImageContainerStyles = (itemBadge) => {
      if (!itemBadge.badge_image && itemBadge.badge_color) {
        return {
          backgroundColor: itemBadge.badge_color,
          border: 'none',
        };
      }
      return {};
    };

    const handleItemClick = () => {
      if (!props.itemId) return;
      const baseUrl = handleMarketplaceUrl();
      window.open(`${baseUrl}/item-detail/${props.itemId}`, '_blank');
    };

    const limitedBadges = computed(() => {
      return props.badges ? props.badges.slice(0, 3) : [];
    });

    const limitedUserBadges = computed(() => {
      return props.userBadges ? props.userBadges.slice(0, 3) : [];
    });

    const tagStyles = computed(() => {
      if (!props.tag || !props.tag.display_color) return {};
      return {
        backgroundColor: hexToRgba(props.tag.display_color, 0.16),
        borderColor: props.tag.display_color,
        color: props.tag.display_color,
      };
    });

    </script>
<template>
  <div class="custom-card">
    <!-- LEFT SECTION -->
    <div class="left-section">
      <div class="header">
        <div class="title-section">
          <div class="title-with-tag">
            <h3 class="title">{{ title || '' }}</h3>
            <span
              v-if="tag"
              class="tag-badge"
              :style="tagStyles"
            >
              {{ tag.name || '' }}
            </span>
          </div>
          <h4 
            v-if="itemTitle" 
            class="item-title"
            @click="handleItemClick"
          >
            <span class="item-title-text">{{ itemTitle }}</span><i class="uil uil-link"></i>
          </h4>
        </div>
        <span
          v-if="badge"
          class="badge"
          :style="badgeStyles"
        >
          {{ badge }}
        </span>
      </div>
      
      <!-- Content -->
      <div class="content">
        <slot name="content">
          {{ truncatedContent }}
        </slot>
      </div>
      
      <div class="footer">
        <slot name="footer">
          <em v-if="footerText" class="footer-text">{{ footerText }}</em>
          <span v-if="username" class="footer-username">by: @{{ username }}</span>
        </slot>
        
        <!-- Item Badges Section -->
        <div v-if="limitedBadges && limitedBadges.length > 0" class="item-badges-section">
          <div class="item-badges-container">
            <div 
              v-for="(itemBadge, index) in limitedBadges" 
              :key="index" 
              class="item-badge"
              :title="itemBadge.name || 'Badge'"
            >
              <div 
                class="item-badge-image-container"
                :style="getItemBadgeImageContainerStyles(itemBadge)"
              >
                <img 
                  v-if="itemBadge.badge_image" 
                  :src="itemBadge.badge_image" 
                  :alt="itemBadge.name || 'Badge'"
                  class="item-badge-image"
                />
                <span 
                  v-else-if="itemBadge.name" 
                  class="item-badge-initials"
                >
                  {{ getBadgeInitials(itemBadge) }}
                </span>
              </div>
              <span 
                v-if="itemBadge.name" 
                class="item-badge-name"
              >
                {{ itemBadge.name }}
              </span>
            </div>
          </div>
        </div>
        
    
        <!-- User Badges Section -->
        <div v-if="limitedUserBadges && limitedUserBadges.length > 0" class="user-badges-section">
          <div class="badges-header">
            <h4 class="badges-title">Credentials & Badges</h4>
            <span class="badges-count">{{ userBadges.length }} badge{{ userBadges.length !== 1 ? 's' : '' }}</span>
          </div>
          <div class="badges-container">
            <div 
              v-for="(badge, index) in limitedUserBadges" 
              :key="index" 
              class="user-badge-card"
            >
              <div class="badge-icon">
                <img 
                  v-if="badge.icon" 
                  :src="badge.icon" 
                  :alt="badge.title || 'Badge'"
                  class="badge-icon-image"
                />
                <slot :name="`badge-icon-${index}`">
                  <div v-if="!badge.icon" class="badge-icon-placeholder"></div>
                </slot>
              </div>
              <div class="badge-content">
                <h5 class="badge-title">{{ badge.title }}</h5>
                <p v-if="badge.subtitle" class="badge-subtitle">{{ badge.subtitle }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="right-section">
      <img 
        v-if="imageSrc" 
        :src="imageSrc" 
        :alt="imageAlt || title"
        class="square-image"
        loading="lazy"
        draggable="false"
        @dragstart.prevent
      />
      <slot name="image">
        <div v-if="!imageSrc" class="square-image placeholder">
          <span>Placeholder</span>
        </div>
      </slot>
    </div>
  </div>
</template>

<style scoped>
.custom-card {
  display: flex;
  width: 100%;
  height: 100%;
  gap: 5rem;
  min-height: 0;
  align-items: stretch;
}

.left-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: hidden;
  align-items: stretch;
  justify-content: space-between;
}

.header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: nowrap;
  flex-shrink: 0;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-shrink: 1;
  min-width: 0;
}

.title-with-tag {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.tag-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.875rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  white-space: nowrap;
  flex-shrink: 0;
}

.tag-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  background: linear-gradient(to bottom right, white, rgba(255, 255, 255, 0.493));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.item-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  color: rgba(255, 255, 255, 0.8);
}

@media (max-width: 768px) {
  .item-title {
    font-size: 1rem;
  }
}

.item-title-text {
  display: inline;
}

.item-title i {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.6);
  margin-left: 0.375rem;
  display: inline-block;
  vertical-align: middle;
}

.item-title:hover {
  color: rgba(255, 255, 255, 1);
  transition: opacity 0.2s ease;
  cursor: pointer;
  text-decoration: underline;
}

.item-title:hover i {
  color: rgba(255, 255, 255, 0.9);
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1.25rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  white-space: nowrap;
  flex-shrink: 0;
}

/* Item Badges Section */
.item-badges-section {
  margin-top: 0;
  padding-top: 0;
  margin-bottom: 0;
}

.item-badges-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.item-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  background: #0F0F0F;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
  flex-shrink: 0;
}

.item-badge-image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  height: 32px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
}

.item-badge-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-badge-initials {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1;
  text-align: center;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.item-badge-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  user-select: none;
}

.content {
  margin-bottom: 1rem;
  min-height: 0;
  overflow: hidden;
  overflow-wrap: break-word;
  word-wrap: break-word;
  text-align: justify;
  text-align-last: left;
  text-justify: inter-word;
}

.footer {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: fit-content;
}

.footer-text {
  display: block;
}

.footer-username {
  display: block;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 0.25rem;
}

.footer-link {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: underline;
  cursor: pointer;
  transition: opacity 0.2s ease;
  font-style: normal;
  display: inline-block;
}

.footer-link:hover {
  opacity: 0.7;
}

/* User Badges Section */
.user-badges-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.badges-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.badges-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 1);
  text-transform: none;
  letter-spacing: normal;
}

.badges-count {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
}

.badges-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.user-badge-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #0F0F0F;
  border: 1px solid #0F0F0F;
  border-radius: 0.75rem;
  flex: 1;
  min-width: 200px;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.user-badge-card:hover {
  background: #0F0F0F;
  border-color: #0F0F0F;
}

.badge-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.badge-icon-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 0.5rem;
}

.badge-icon-placeholder {
  width: 100%;
  height: 100%;
  background: #0F0F0F;
  border-radius: 0.5rem;
  border: 1px dashed #0F0F0F;
}

.badge-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.badge-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 1);
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.badge-subtitle {
  margin: 0;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.right-section {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  width: 300px;
  aspect-ratio: 1 / 1;
}

.square-image {
  width: 100%;
  max-width: 300px;
  height: 100%;
  aspect-ratio: 1 / 1;
  border: 1px solid #0F0F0F;
  object-fit: cover;
  border-radius: 1rem;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  pointer-events: auto;
}

.square-image.placeholder {
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.9);
  border: 1px dashed #0F0F0F;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
}

@media (max-width: 1024px) {
  .custom-card {
    gap: 0.875rem;
  }
  
}

@media (max-width: 768px) {
  .custom-card {
    flex-direction: column;
    gap: 1rem;
  }
  
  .right-section {
    width: 100%;
    max-width: 300px;
    aspect-ratio: 1 / 1;
    align-self: center;
  }

  
  .header {
    gap: 0.5rem;
  }
  
  .title {
    font-size: 1.125rem;
  }
  
  .tag-badge {
    padding: 0.3rem 0.75rem;
    font-size: 0.7rem;
    gap: 0.375rem;
  }
  
  .tag-icon {
    width: 14px;
    height: 14px;
  }
}

@media (max-width: 640px) {
  .custom-card {
    gap: 0.875rem;
  }
  
  .title {
    font-size: 1rem;
    letter-spacing: 0.05em;
  }
  
  .tag-badge {
    padding: 0.25rem 0.625rem;
    font-size: 0.65rem;
    gap: 0.25rem;
  }
  
  .tag-icon {
    width: 12px;
    height: 12px;
  }
  
  .badge {
    padding: 0.2rem 0.625rem;
    font-size: 0.7rem;
  }
  
  .item-badge {
    padding: 0.3rem 0.625rem;
    gap: 0.375rem;
  }
  
  .item-badge-image-container {
    width: 20px;
    height: 20px;
  }
  
  .item-badge-initials {
    font-size: 0.65rem;
  }
  
  .item-badge-name {
    font-size: 0.8rem;
  }
  
  .content {
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
  }
  
  .footer {
    font-size: 0.8rem;
  }
  
  .user-badge-card {
    min-width: 100%;
  }
  
  .badges-container {
    flex-direction: column;
  }

}

@media (max-width: 480px) {
  .header {
    gap: 0.5rem;
  }
  
  .title {
    font-size: 0.9rem;
  }
  
  .tag-badge {
    padding: 0.2rem 0.5rem;
    font-size: 0.6rem;
    gap: 0.25rem;
  }
  
  .tag-icon {
    width: 10px;
    height: 10px;
  }
  
  .content {
    font-size: 0.85rem;
  }
  
  .footer {
    font-size: 0.75rem;
  }
  
  .badges-title {
    font-size: 0.9rem;
  }
  
  .badges-count {
    font-size: 0.8rem;
  }
  
  .user-badge-card {
    padding: 0.75rem;
    gap: 0.75rem;
  }
  
  .badge-icon {
    width: 40px;
    height: 40px;
  }
  
  .badge-title {
    font-size: 0.8rem;
  }
  
  .badge-subtitle {
    font-size: 0.7rem;
  }

}

@media (prefers-reduced-motion: reduce) {
  .custom-card * {
    transition: none !important;
  }
}
</style>
